import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { useAuthToken } from "../TokenContext";
import axios from 'axios';
import { useHistory } from 'react-router-dom';
import { useIntl } from "react-intl";
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Alert,
  Divider,
  CircularProgress,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  TablePagination,
  InputAdornment,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  Description as DescriptionIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Sort as SortIcon,
  LocationOn as LocationIcon,
  ViewModule as GridViewIcon,
  ViewList as ListViewIcon,
} from '@mui/icons-material';

// API Endpoints
const API_ENDPOINTS = {
  CREATE_GIG: process.env.REACT_APP_CLIENT_CREATEAGIG,
  FETCH_GIG: process.env.REACT_APP_CLIENT_FETCHGIG,
  EDIT_GIG: process.env.REACT_APP_CLIENT_EDITGIGMANAGER,
  DELETE_GIG: process.env.REACT_APP_CLIENT_DELETEGIGMANAGER,
  FETCH_LOCATIONS: 'https://f62bxmx6oe.execute-api.us-east-1.amazonaws.com/dev/fetchGloballocations'
};

// Separate Edit Dialog Component for better isolation
const EditGigDialog = ({ open, onClose, onSave, gig, locations, loading }) => {
  const { user } = useSelector(state => state.auth);
    const intl = useIntl();
  
  const [formData, setFormData] = useState({
    solution_id: '',
    solution_Name: '',
    solution_Description: '',
    Organization_Code: '',
    global_id: '',
    global_location_name: ''
  });
  
  const [formErrors, setFormErrors] = useState({
    solution_Name: false,
    solution_Description: false,
    // global_id: false
  });

  // Update form data when gig changes
  useEffect(() => {
    if (gig) {
      setFormData({
        solution_id: gig.solution_id || gig.id || '',
        solution_Name: gig.solution_Name || '',
        solution_Description: gig.solution_Description || '',
        Organization_Code: user?.data?.[0]?.client_code || '',
        global_id: gig.global_id || '',
        global_location_name: gig.global_location_name || ''
      });
    }
  }, [gig, user]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: false
      }));
    }
  };

  const handleLocationChange = (e) => {
    const locationId = e.target.value;
    const selectedLocation = locations.find(loc => loc.global_id === locationId);
    
    if (selectedLocation) {
      setFormData(prev => ({
        ...prev,
        global_id: selectedLocation.global_id,
        global_location_name: selectedLocation.global_location_name
      }));
      
      if (formErrors.global_id) {
        setFormErrors(prev => ({
          ...prev,
          global_id: false
        }));
      }
    }
  };

  const validateForm = () => {
    const errors = {
      solution_Name: !formData.solution_Name,
      solution_Description: !formData.solution_Description,
      // global_id: !formData.global_id
    };
    
    setFormErrors(errors);
    return !Object.values(errors).some(error => error);
  };

  const handleSave = () => {
    if (validateForm()) {
      onSave(formData);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">{intl.formatMessage({ id: "editSolution" })}</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Box component="form" noValidate sx={{ mt: 1 }}>
          <TextField
            margin="normal"
            required
            fullWidth
            id="solution_Name"
            label={intl.formatMessage({ id: "solutionName" })}
            name="solution_Name"
            value={formData.solution_Name}
            onChange={handleInputChange}
            inputProps={{ maxLength: 50 }}
            helperText={formErrors.solution_Nameolution_Name ? "Solution Name is required" : `${formData.solution_Name.length}/50`}
            error={formErrors.solution_Name}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            multiline
            rows={4}
            id="solution_Description"
            label={intl.formatMessage({ id: "solutionDescription" })}
            name="solution_Description"
            value={formData.solution_Description}
            onChange={handleInputChange}
            inputProps={{ maxLength: 200 }}
            helperText={formErrors.solution_Description ? "Gig Description is required" : `${formData.solution_Description.length}/200`}
            error={formErrors.solution_Description}
          />
          {/* <FormControl 
            fullWidth 
            margin="normal" 
            required
            error={formErrors.global_id}
          >
            <InputLabel id="global-location-label">Location</InputLabel>
            <Select
              labelId="global-location-label"
              id="global_id"
              name="global_id"
              value={formData.global_id}
              onChange={handleLocationChange}
              label="Location"
              startAdornment={
                <InputAdornment position="start">
                  <LocationIcon />
                </InputAdornment>
              }
            >
              {locations.length === 0 ? (
                <MenuItem disabled>No locations available</MenuItem>
              ) : (
                locations.map((location) => (
                  <MenuItem key={location.global_id} value={location.global_id}>
                    {location.global_location_name}
                  </MenuItem>
                ))
              )}
            </Select>
             {formErrors.global_id && <FormHelperText>Location is required</FormHelperText>} 
          </FormControl> */}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>{intl.formatMessage({ id: "cancel" })}</Button>
        <Button 
          variant="contained" 
          onClick={handleSave} 
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {intl.formatMessage({ id: "updateSolution" })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Create Gig Dialog Component
const CreateGigDialog = ({ open, onClose, onSave, locations, loading }) => {
  const { user } = useSelector(state => state.auth);
    const intl = useIntl();
  
  const [formData, setFormData] = useState({
    solution_Name: '',
    solution_Description: '',
    Organization_Code: '',
    global_id: '',
    global_location_name: ''
  });
  
  const [formErrors, setFormErrors] = useState({
    solution_Name: false,
    solution_Description: false,
  //  global_id: false
  });

  // Initialize organization code when user data changes
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      Organization_Code: user?.data?.[0]?.client_code || ''
    }));
  }, [user]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: false
      }));
    }
  };

  const handleLocationChange = (e) => {
    const locationId = e.target.value;
    const selectedLocation = locations.find(loc => loc.global_id === locationId);
    
    if (selectedLocation) {
      setFormData(prev => ({
        ...prev,
        global_id: selectedLocation.global_id,
        global_location_name: selectedLocation.global_location_name
      }));
      
      if (formErrors.global_id) {
        setFormErrors(prev => ({
          ...prev,
          global_id: false
        }));
      }
    }
  };

  const validateForm = () => {
    const errors = {
      solution_Name: !formData.solution_Name,
      solution_Description: !formData.solution_Description,
     // global_id: !formData.global_id
    };
    
    setFormErrors(errors);
    return !Object.values(errors).some(error => error);
  };

  const handleSave = () => {
    if (validateForm()) {
      onSave(formData);
    }
  };

  // Reset form when dialog closes
  useEffect(() => {
    if (!open) {
      setFormData({
        solution_Name: '',
        solution_Description: '',
        Organization_Code: user?.data?.[0]?.client_code || '',
        global_id: '',
        global_location_name: ''
      });
      setFormErrors({
        solution_Name: false,
        solution_Description: false,
        global_id: false
      });
    }
  }, [open, user]);

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6"> {intl.formatMessage({ id: "createNewSolution" })}</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Box component="form" noValidate sx={{ mt: 1 }}>
          <TextField
            margin="normal"
            required
            fullWidth
            id="solution_Name"
            label= {intl.formatMessage({ id: "solutionName" })}
            name="solution_Name"
            value={formData.solution_Name}
            onChange={handleInputChange}
            inputProps={{ maxLength: 50 }}
            // helperText={formErrors.solution_Name ? "Solution Name is required" : `${formData.solution_Name.length}/50`}
            // error={formErrors.solution_Name}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            multiline
            rows={4}
            id="solution_Description"
            label={intl.formatMessage({ id: "solutionDescription" })}
            name="solution_Description"
            value={formData.solution_Description}
            onChange={handleInputChange}
            inputProps={{ maxLength: 200 }}
            helperText={formErrors.solution_Description ? "Solution Description is required" : `${formData.solution_Description.length}/200`}
            error={formErrors.solution_Description}
          />
          {/* <FormControl 
            fullWidth 
            margin="normal" 
            required
            error={formErrors.global_id}
          >
            <InputLabel id="global-location-label">Location</InputLabel>
            <Select
              labelId="global-location-label"
              id="global_id"
              name="global_id"
              value={formData.global_id}
              onChange={handleLocationChange}
              label="Location"
              startAdornment={
                <InputAdornment position="start">
                  <LocationIcon />
                </InputAdornment>
              }
            >
              {locations.length === 0 ? (
                <MenuItem disabled>No locations available</MenuItem>
              ) : (
                locations.map((location) => (
                  <MenuItem key={location.global_id} value={location.global_id}>
                    {location.global_location_name}
                  </MenuItem>
                ))
              )}
            </Select>
             {formErrors.global_id && <FormHelperText>Location is required</FormHelperText>} 
          </FormControl> */}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>  {intl.formatMessage({ id: "cancel" })}</Button>
        <Button 
          variant="contained" 
          onClick={handleSave} 
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
         {intl.formatMessage({ id: "createSolution" })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Main Component
const CreateSolution = ({collapsed}) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { isLoggedIn, user } = useSelector(state => state.auth);
  const token = useAuthToken();
    const intl = useIntl();
  
  // State declarations
  const [gigs, setGigs] = useState([]);
  const [filteredGigs, setFilteredGigs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [gigToDelete, setGigToDelete] = useState(null);
  const [gigToEdit, setGigToEdit] = useState(null);
  const [viewGig, setViewGig] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });
  
  // New state for locations
  const [locations, setLocations] = useState([]);
  const [fetchingLocations, setFetchingLocations] = useState(false);
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(9);
  
  // Search and filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState('solution_Name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [viewMode, setViewMode] = useState('grid');
  
  // Menu state
  const [sortMenuAnchor, setSortMenuAnchor] = useState(null);
  
  // Fetch gigs on component mount
  useEffect(() => {
    if (user?.data?.[0]?.client_code && token) {
      fetchGigs();
      fetchLocations();
    }
  }, [user?.data, token]);
  
  // Apply filtering and sorting
  useEffect(() => {
    let result = [...gigs];
    
    // Apply search
    if (searchQuery) {
      const lowerQuery = searchQuery.toLowerCase();
      result = result.filter(gig => 
        gig.solution_Name?.toLowerCase().includes(lowerQuery) || 
        gig.solution_Description?.toLowerCase().includes(lowerQuery) ||
        (gig.global_location_name && gig.global_location_name.toLowerCase().includes(lowerQuery))
      );
    }
    
    // Apply sorting
    result.sort((a, b) => {
      let fieldA = a[sortField]?.toLowerCase() || '';
      let fieldB = b[sortField]?.toLowerCase() || '';
      
      if (sortDirection === 'asc') {
        return fieldA.localeCompare(fieldB);
      } else {
        return fieldB.localeCompare(fieldA);
      }
    });
    
    setFilteredGigs(result);
    setPage(0); // Reset to first page when filters change
  }, [gigs, searchQuery, sortField, sortDirection]);
  
  // Fetch gigs function
  const fetchGigs = async () => {
    if (!user?.data?.[0]?.client_code || !token) return;
    
    setLoading(true);
    try {
      const headers = {
        'Authorization': token,
        'Content-Type': 'application/json',
      };
      
      const response = await axios.post(
        API_ENDPOINTS.FETCH_GIG, 
        {
          Organization_Code: user.data[0].client_code,
        },
        { headers }
      );
      
      if (response.data.body.statuscode === 200) {
        console.log("Fetched gigs:", response.data.body.body);
        setGigs(response.data.body.body);
        setFilteredGigs(response.data.body.body);
      }
    } catch (error) {
      console.error('Error fetching gigs:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load gigs. Please try again.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch locations function
  const fetchLocations = async () => {
    if (!user?.data?.[0]?.client_code || !token) return;
    
    setFetchingLocations(true);
    try {
      const headers = {
        'Authorization': token.startsWith('Bearer ') ? token : `Bearer ${token}`,
        'Content-Type': 'application/json',
      };
      
      const response = await axios.post(
        API_ENDPOINTS.FETCH_LOCATIONS, 
        {
          Organization_Code: user.data[0].client_code,
        },
        { headers }
      );
      
      // Check if the response contains locations
      if (response.data && 
          response.data.body && 
          response.data.body.body && 
          Array.isArray(response.data.body.body)) {
        
        console.log("Fetched locations:", response.data.body.body);
        setLocations(response.data.body.body);
      } else {
        console.warn('Unexpected locations response format:', response.data);
        setLocations([]);
      }
    } catch (error) {
      console.error('Error fetching locations:', error);
      setSnackbar({
        open: true,
        // message: 'Failed to load locations. Some features may be limited.',
        // severity: 'warning'
      });
    } finally {
      setFetchingLocations(false);
    }
  };
  
  // Handle create gig (receives form data from dialog)
  const handleCreateGig = async (formData) => {    
    setLoading(true);
    try {
      console.log("Creating Solution with data:", formData);
      
      const response = await fetch(API_ENDPOINTS.CREATE_GIG, {
        method: 'POST',
        headers: {
          'Authorization': token,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          solution_Name: formData.solution_Name,
          solution_Description: formData.solution_Description,
          Organization_Code: user.data[0].client_code,
          global_id: formData.global_id,
          global_location_name: formData.global_location_name
        }),
      });
      
      if (response.ok) {
        setSnackbar({
          open: true,
          message: 'Solution created successfully!',
          severity: 'success'
        });
        setCreateDialogOpen(false);
        fetchGigs(); // Refresh gig list
      } else {
        throw new Error('Failed to create Solution');
      }
    } catch (error) {
      console.error('Error creating Solution:', error);
      setSnackbar({
        open: true,
        message: 'Failed to create Solution. Please try again.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };
  
  // Handle edit gig (receives form data from dialog)
  // Handle edit gig (receives form data from dialog)
const handleEditGig = async (formData) => {    
  setLoading(true);
  try {
    console.log("Updating Solution with data:", formData);
    
    // Use the specific edit endpoint for updates
    const response = await fetch(API_ENDPOINTS.EDIT_GIG, {
      method: 'PUT',
      headers: {
        'Authorization': token,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        solution_id: formData.solution_id,
        solution_Name: formData.solution_Name,
        solution_Description: formData.solution_Description,
        Organization_Code: user.data[0].client_code,
        global_id: formData.global_id,
        global_location_name: formData.global_location_name
      }),
    });
    
    if (response.ok) {
      setSnackbar({
        open: true,
        message: 'Solution updated successfully!',
        severity: 'success'
      });
      setEditDialogOpen(false);
      setGigToEdit(null);
      fetchGigs(); // Refresh gig list
    } else {
      throw new Error('Failed to update Solution');
    }
  } catch (error) {
    console.error('Error updating Solution:', error);
    setSnackbar({
      open: true,
      message: `Failed to update Solution: ${error.message}`,
      severity: 'error'
    });
  } finally {
    setLoading(false);
  }
};

  // Handle delete gig
  const handleDeleteGig = async () => {
    if (!gigToDelete || !(gigToDelete.solution_id || gigToDelete.id)) {
      setSnackbar({
        open: true,
        message: 'Invalid Solution selected for deletion.',
        severity: 'error'
      });
      return;
    }
    
    setLoading(true);
    try {
      const headers = {
        'Authorization': token,
        'Content-Type': 'application/json',
        'Date': new Date().toUTCString()
      };
      
      const response = await axios.post(
        API_ENDPOINTS.DELETE_GIG,
        {
          solution_id: gigToDelete.solution_id || gigToDelete.id
        },
        { headers }
      );
      
      if (response.status === 200) {
        setSnackbar({
          open: true,
          message: 'Solution deleted successfully!',
          severity: 'success'
        });
        setDeleteDialogOpen(false);
        setGigToDelete(null);
        fetchGigs(); // Refresh gig list
      } else {
        throw new Error('Failed to delete Solution');
      }
    } catch (error) {
      console.error('Error deleting Solution:', error);
      setSnackbar({
        open: true,
        message: `Failed to delete Solution: ${error.response?.data?.message || error.message}`,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };
  
  // Open delete confirmation dialog
  const openDeleteDialog = (gig) => {
    setGigToDelete(gig);
    setDeleteDialogOpen(true);
  };
  
  // Open edit dialog with gig data
  const openEditDialog = useCallback((gig) => {
    console.log("Opening edit dialog for Solution:", gig);
    setGigToEdit(gig);
    setEditDialogOpen(true);
  }, []);
  
  // Open view dialog with gig data
  const openViewDialog = (gig) => {
    setViewGig(gig);
    setViewDialogOpen(true);
  };
  
  // Handle close snackbar
  const handleCloseSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false
    });
  };
  
  // Handle pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  // Handle sort menu
  const handleSortMenuOpen = (event) => {
    setSortMenuAnchor(event.currentTarget);
  };
  
  const handleSortMenuClose = () => {
    setSortMenuAnchor(null);
  };
  
  const handleSort = (field) => {
    if (field === sortField) {
      // Toggle direction if same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New field, set to ascending
      setSortField(field);
      setSortDirection('asc');
    }
    handleSortMenuClose();
  };
  
  // Get paginated data
 // Get paginated data
const getPaginatedData = () => {
  return filteredGigs.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
};

// Render gig cards (grid view)
const renderGigCards = () => {
  if (loading && gigs.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }
  
  
  if (filteredGigs.length === 0) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body1" color="textSecondary">
          {searchQuery ? 'No Solution match your search criteria.' : 'No Solution found. Create your first Solution by clicking the "Create New Solution" button.'}
        </Typography>
      </Paper>
    );
  }
  
  return (
    <Grid container spacing={3}>
      {getPaginatedData().map((gig) => (
        <Grid item xs={12} sm={6} md={4} key={gig.id || gig.solution_id}>
          <Card 
            elevation={3} 
            sx={{ 
              height: '100%', 
              display: 'flex', 
              flexDirection: 'column',
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-5px)',
                boxShadow: '0 10px 20px rgba(0,0,0,0.1)'
              }
            }}
          >
            <CardContent sx={{ flexGrow: 1 }}>
              <Typography variant="h6" component="h2" gutterBottom noWrap>
                {gig.solution_Name}
              </Typography>
              <Typography 
                variant="body2" 
                color="textSecondary" 
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                }}
              >
                {gig.solution_Description}
              </Typography>
              {gig.global_location_name && (
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                  <LocationIcon color="primary" fontSize="small" sx={{ mr: 0.5 }} />
                  <Typography variant="body2" color="primary">
                    {gig.global_location_name}
                  </Typography>
                </Box>
              )}
            </CardContent>
            <CardActions sx={{ justifyContent: 'flex-end', p: 2 }}>
              <Tooltip title= {intl.formatMessage({ id: "viewDetails" })}>
                <IconButton 
                  size="small" 
                  color="primary"
                  onClick={() => openViewDialog(gig)}
                >
                  <DescriptionIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title= {intl.formatMessage({ id: "edit" })}>
                <IconButton 
                  size="small" 
                  color="primary"
                  onClick={() => openEditDialog(gig)}
                >
                  <EditIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title={parseInt(gig.delete_check) === 1 ?  intl.formatMessage({ id: "delete" }):intl.formatMessage({ id: "cannotDeleteAssociatedWithARTs" })}>
                <span>
                  <IconButton 
                    size="small" 
                    color="error"
                    onClick={() => openDeleteDialog(gig)}
                    disabled={parseInt(gig.delete_check) === 0}
                  >
                    <DeleteIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </CardActions>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
};

// Render gig list (list view)
const renderGigList = () => {
  if (loading && gigs.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }
  
  if (filteredGigs.length === 0) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body1" color="textSecondary">
          {searchQuery ? 'No Solution match your search criteria.' : 'No Solution found. Create your first gig by clicking the "Create New Gig" button.'}
        </Typography>
      </Paper>
    );
  }
  
  return (
    <Paper elevation={2}>
      <List>
        {getPaginatedData().map((gig, index) => (
          <React.Fragment key={gig.id || gig.solution_id}>
            <ListItem 
              alignItems="flex-start"
              sx={{ 
                py: 2,
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)'
                }
              }}
              secondaryAction={
                <Box>
                  <Tooltip title="View Details">
                    <IconButton 
                      edge="end" 
                      onClick={() => openViewDialog(gig)}
                      color="primary"
                    >
                      <DescriptionIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Edit">
                    <IconButton 
                      edge="end" 
                      onClick={() => openEditDialog(gig)}
                      color="primary"
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={parseInt(gig.delete_check) === 1 ? "Delete" : "Cannot delete - associated with ARTs"}>
                    <span>
                      <IconButton 
                        edge="end" 
                        onClick={() => openDeleteDialog(gig)}
                        color="error"
                        disabled={parseInt(gig.delete_check) === 0}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              }
            >
              <ListItemText
                primary={
                  <Typography variant="h6" component="div">
                    {gig.solution_Name}
                  </Typography>
                }
                secondary={
                  <React.Fragment>
                    <Typography
                      component="span"
                      variant="body2"
                      color="textSecondary"
                      sx={{
                        display: 'inline-block',
                        maxWidth: '80%',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        mb: 0.5
                      }}
                    >
                      {gig.solution_Description}
                    </Typography>
                    {gig.global_location_name && (
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                        <LocationIcon color="primary" fontSize="small" sx={{ mr: 0.5 }} />
                        <Typography variant="body2" color="primary">
                          {gig.global_location_name}
                        </Typography>
                      </Box>
                    )}
                  </React.Fragment>
                }
              />
            </ListItem>
            {index < getPaginatedData().length - 1 && <Divider component="li" />}
          </React.Fragment>
        ))}
      </List>
    </Paper>
  );
};

return (
  <Box
        sx={{
          marginLeft: collapsed ? "60px" : "240px", // Account for sidebar
          width: collapsed ? "calc(100% - 60px)" : "calc(100% - 240px)", // Available space
          display: 'flex',
          justifyContent: 'center', // Center the container
          px: 2,
        }}
      >
        <Container 
          maxWidth="lg" 
          sx={{ 
            mt: 10, 
            mb: 4,
            width: '100%',
          }}
      >
    <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <Typography variant="h4" component="h1" gutterBottom>
        {intl.formatMessage({ id: "solutionManagement" })}
      </Typography>
      <Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={() => {
            fetchGigs();
            fetchLocations();
          }}
          sx={{ mr: 2 }}
        >
             {intl.formatMessage({ id: "refresh" })}
        </Button>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateDialogOpen(true)}
        >
          {intl.formatMessage({ id: "createNewSolution" })}
        </Button>
      </Box>
    </Box>
    
    {/* Toolbar with search and filters */}
    <Paper sx={{ p: 2, mb: 3 }}>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            placeholder= {intl.formatMessage({ id: "searchSolutions" })}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            variant="outlined"
            size="small"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Box display="flex" justifyContent="flex-end">
            <Button
              variant="outlined"
              startIcon={<SortIcon />}
              onClick={handleSortMenuOpen}
              sx={{ mr: 2 }}
              size="small"
            >
              {intl.formatMessage({ id: "sortBy" })}
            </Button>
            <Menu
              anchorEl={sortMenuAnchor}
              open={Boolean(sortMenuAnchor)}
              onClose={handleSortMenuClose}
            >
              <MenuItem 
                onClick={() => handleSort('solution_Name')}
                selected={sortField === 'solution_Name'}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                   {intl.formatMessage({ id: "name" })}
                  {sortField === 'solution_Name' && (
                    <Typography variant="caption" color="primary">
                      {sortDirection === 'asc' ? '(A-Z)' : '(Z-A)'}
                    </Typography>
                  )}
                </Box>
              </MenuItem>
              <MenuItem 
                onClick={() => handleSort('solution_Description')}
                selected={sortField === 'solution_Description'}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                      {intl.formatMessage({ id: "description" })}
                  {sortField === 'solution_Description' && (
                    <Typography variant="caption" color="primary">
                      {sortDirection === 'asc' ? '(A-Z)' : '(Z-A)'}
                    </Typography>
                  )}
                </Box>
              </MenuItem>
              <MenuItem 
                onClick={() => handleSort('global_location_name')}
                selected={sortField === 'global_location_name'}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                  Location
                  {sortField === 'global_location_name' && (
                    <Typography variant="caption" color="primary">
                      {sortDirection === 'asc' ? '(A-Z)' : '(Z-A)'}
                    </Typography>
                  )}
                </Box>
              </MenuItem>
            </Menu>
            <Tooltip title="Toggle View">
              <IconButton 
                onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
                color="primary"
                size="small"
              >
                {viewMode === 'grid' ? <ListViewIcon /> : <GridViewIcon />}
              </IconButton>
            </Tooltip>
          </Box>
        </Grid>
      </Grid>
    </Paper>
    
    {/* Display gigs based on view mode */}
    {viewMode === 'grid' ? renderGigCards() : renderGigList()}
    
    {/* Pagination */}
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
      <TablePagination
        component="div"
        count={filteredGigs.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[9, 18, 36, 99]}
        labelRowsPerPage="Solutions per page:"
        showFirstButton
        showLastButton
      />
    </Box>
    
    {/* Create Gig Dialog - Using separated component */}
    <CreateGigDialog
      open={createDialogOpen}
      onClose={() => setCreateDialogOpen(false)}
      onSave={handleCreateGig}
      locations={locations}
      loading={loading}
    />
    
    {/* Edit Gig Dialog - Using separated component */}
    <EditGigDialog
      open={editDialogOpen}
      onClose={() => setEditDialogOpen(false)}
      onSave={handleEditGig}
      gig={gigToEdit}
      locations={locations}
      loading={loading}
    />
    
    {/* View Gig Dialog */}
    <Dialog
      open={viewDialogOpen}
      onClose={() => setViewDialogOpen(false)}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Solution Details</Typography>
          <IconButton onClick={() => setViewDialogOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {viewGig && (
          <List>
            <ListItem>
              <ListItemText 
                primary="Gig ID" 
                secondary={viewGig.id || viewGig.solution_id} 
                primaryTypographyProps={{ variant: 'subtitle2', color: 'textSecondary' }}
                secondaryTypographyProps={{ variant: 'body1' }}
              />
            </ListItem>
            <Divider component="li" />
            <ListItem>
              <ListItemText 
                primary="Gig Name" 
                secondary={viewGig.solution_Name} 
                primaryTypographyProps={{ variant: 'subtitle2', color: 'textSecondary' }}
                secondaryTypographyProps={{ variant: 'body1' }}
              />
            </ListItem>
            <Divider component="li" />
            <ListItem sx={{ flexDirection: 'column', alignItems: 'flex-start' }}>
              <ListItemText 
                primary="Gig Description" 
                primaryTypographyProps={{ variant: 'subtitle2', color: 'textSecondary' }}
                sx={{ mb: 1 }}
              />
              <Typography variant="body1" sx={{ width: '100%' }}>
                {viewGig.solution_Description}
              </Typography>
            </ListItem>
            <Divider component="li" />
            {viewGig.global_location_name && (
              <>
                <ListItem>
                  <ListItemText 
                    primary="Location" 
                    secondary={viewGig.global_location_name} 
                    primaryTypographyProps={{ variant: 'subtitle2', color: 'textSecondary' }}
                    secondaryTypographyProps={{ variant: 'body1' }}
                  />
                </ListItem>
                <Divider component="li" />
              </>
            )}
            <ListItem>
              <ListItemText 
                primary="Organization Code" 
                secondary={viewGig.Organization_Code} 
                primaryTypographyProps={{ variant: 'subtitle2', color: 'textSecondary' }}
                secondaryTypographyProps={{ variant: 'body1' }}
              />
            </ListItem>
          </List>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={() => setViewDialogOpen(false)}>{intl.formatMessage({ id: "close" })}</Button>
        {viewGig && parseInt(viewGig.delete_check) === 1 && (
          <Button 
            variant="outlined" 
            color="error"
            onClick={() => {
              setViewDialogOpen(false);
              openDeleteDialog(viewGig);
            }}
          >
            {intl.formatMessage({ id: "delete" })}
          </Button>
        )}
      </DialogActions>
    </Dialog>
    
    {/* Delete Confirmation Dialog */}
    <Dialog
      open={deleteDialogOpen}
      onClose={() => setDeleteDialogOpen(false)}
    >
      <DialogTitle>
       {intl.formatMessage({ id: "confirmDeletion" })}
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
              {intl.formatMessage({ id: "confirmSolutionDeletion" })} "{gigToDelete?.solution_Name}"? This action cannot be undone.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
        <Button 
          color="error" 
          variant="contained"
          onClick={handleDeleteGig}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
              {intl.formatMessage({ id: "delete" })}
        </Button>
      </DialogActions>
    </Dialog>
    
    {/* Snackbar for notifications */}
    <Snackbar
      open={snackbar.open}
      autoHideDuration={6000}
      onClose={handleCloseSnackbar}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
    >
      <Alert 
        onClose={handleCloseSnackbar} 
        severity={snackbar.severity} 
        sx={{ width: '100%' }}
        variant="filled"
      >
        {snackbar.message}
      </Alert>
    </Snackbar>
  </Container>
  </Box>
);
}
  export default CreateSolution;  